import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { detectInstallPlatform, isInAppBrowser, requestAppInstall, useAppInstall } from '@/hooks/useAppInstall';

export default function InstallPage() {
  const { installed, installing, prompt } = useAppInstall();
  const actualPlatform = detectInstallPlatform();
  const [platform, setPlatform] = useState<'android' | 'ios'>(actualPlatform === 'ios' ? 'ios' : 'android');
  const [message, setMessage] = useState('');
  const [sharing, setSharing] = useState(false);
  const guideRef = useRef<HTMLElement | null>(null);
  const linkRef = useRef<HTMLInputElement | null>(null);
  const installUrl = `${window.location.origin}/install`;
  const inApp = isInAppBrowser();
  const canInstall = Boolean(prompt) && actualPlatform === 'android' && !inApp && platform === 'android';
  const steps = platform === 'ios'
    ? [
      ['ri-compass-3-line', 'Safari에서 이 링크 열기', '카카오톡 등에서 열었다면 메뉴의 ‘Safari에서 열기’를 선택하세요. 보이지 않으면 링크를 복사해 Safari 주소창에 붙여넣으세요.'],
      ['ri-share-forward-line', '공유 → 홈 화면에 추가', 'Safari의 공유 버튼을 누르세요. 공유 버튼이 보이지 않으면 페이지 메뉴에서 ‘공유’를 찾을 수 있어요. 목록을 내려 ‘홈 화면에 추가’를 선택하세요.'],
      ['ri-add-box-line', '‘추가’를 누르면 완료', '‘웹 앱으로 열기’가 보이면 켜고 ‘추가’를 누르세요. 홈 화면의 Family Album 아이콘으로 바로 열 수 있어요.'],
    ]
    : [
      ['ri-chrome-line', 'Chrome에서 이 링크 열기', '카카오톡 등에서 열었다면 메뉴의 ‘다른 브라우저로 열기’를 선택하거나, 링크를 복사해 Chrome 주소창에 붙여넣으세요.'],
      ['ri-download-2-line', '앱 설치 또는 홈 화면에 추가', '위의 설치 버튼이 보이면 눌러주세요. 버튼이 없으면 Chrome의 점 세 개 메뉴에서 ‘홈 화면에 추가’ 또는 ‘앱 설치’를 찾아주세요.'],
      ['ri-smartphone-line', '설치하고 홈 화면에서 열기', '안내에 따라 설치를 완료한 뒤 Family Album 아이콘을 눌러주세요. 기기에 따라 메뉴 이름은 조금 다를 수 있어요.'],
    ];

  const copyLink = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('clipboard-unavailable');
      await navigator.clipboard.writeText(installUrl);
      setMessage('설치 링크를 복사했어요. 가족에게 붙여넣어 보내주세요.');
    } catch {
      linkRef.current?.focus();
      linkRef.current?.select();
      setMessage('아래 링크를 길게 누르거나 선택해서 복사해 주세요.');
    }
  };

  const share = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Family Album 설치', text: '우리 가족 앨범을 휴대폰 홈 화면에 추가해 주세요.', url: installUrl });
      } else await copyLink();
    } catch (error) {
      if ((error as Error).name !== 'AbortError') await copyLink();
    } finally { setSharing(false); }
  };

  const install = async () => {
    setMessage('');
    if (canInstall) {
      const result = await requestAppInstall();
      if (result === 'accepted') setMessage('설치를 요청했어요. 휴대폰의 안내를 마치고 홈 화면을 확인해 주세요.');
      else if (result === 'dismissed') setMessage('설치창을 닫았어요. 아래 방법으로 나중에 다시 설치할 수 있어요.');
      else setMessage('아래 안내에 따라 브라우저 메뉴에서 설치해 주세요.');
    }
    guideRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-background-50 text-foreground-950">
      <div className="mx-auto max-w-lg px-5 pb-[max(28px,env(safe-area-inset-bottom))] pt-[max(22px,env(safe-area-inset-top))]">
        <header className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm font-bold"><img src="/icon-192.png" alt="" className="h-8 w-8 rounded-lg" />Family Album</Link>
          <Link to="/login" className="rounded-full border border-background-300 px-4 py-2 text-xs font-semibold">로그인</Link>
        </header>

        <section className="pb-7 pt-10 text-center">
          <img src="/icon-192.png" alt="Family Album 앱 아이콘" className="mx-auto h-24 w-24 rounded-3xl shadow-lg" />
          <p className="mt-6 text-xs font-semibold tracking-widest text-accent-600">가족의 추억을 더 가까이</p>
          <h1 className="mt-3 text-3xl font-bold leading-tight">우리 가족 앨범을<br />휴대폰에 담아두세요</h1>
          <p className="mt-4 text-sm leading-6 text-foreground-500">홈 화면에 아이콘을 추가하면<br />사진과 동영상을 앱처럼 바로 열 수 있어요.</p>
          <div className="mt-5 flex justify-center gap-2 text-xs text-foreground-600"><span className="rounded-full bg-secondary-100 px-3 py-1.5">Android</span><span className="rounded-full bg-secondary-100 px-3 py-1.5">iPhone · iPad</span></div>
        </section>

        {installed ? (
          <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-center">
            <p className="font-semibold text-emerald-800">설치된 앱으로 사용 중이에요</p>
            <Link to="/" className="mt-3 inline-block rounded-xl bg-foreground-900 px-6 py-3 text-sm font-semibold text-white">가족 앨범 열기</Link>
          </section>
        ) : (
          <>
            <div role="tablist" aria-label="휴대폰 종류" className="grid grid-cols-2 gap-1 rounded-2xl bg-secondary-100 p-1">
              {(['android', 'ios'] as const).map(value => <button key={value} id={`install-tab-${value}`} type="button" role="tab" aria-selected={platform === value} aria-controls="install-guide" onClick={() => { setPlatform(value); setMessage(''); }} className={`rounded-xl py-3 text-sm font-semibold ${platform === value ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-500'}`}>{value === 'android' ? '안드로이드' : '아이폰 · 아이패드'}</button>)}
            </div>
            {platform === 'android' ? (
              <section className="mt-5 rounded-3xl border border-accent-200 bg-accent-50 p-5">
                <h2 className="text-lg font-bold">안드로이드 APK로 설치하기</h2>
                <p className="mt-2 text-sm leading-6 text-foreground-600">앱 파일을 내려받아 설치하면 홈 화면에 Family Album 아이콘이 생겨요.</p>
                <a href="/downloads/family-album-1.0.0.apk" download="family-album-1.0.0.apk" className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-foreground-900 px-4 py-4 text-base font-semibold text-white"><i className="ri-android-fill" aria-hidden="true" />APK 다운로드</a>
                <p className="mt-2 text-center text-xs text-foreground-500">앱 v1.0.0 · Android 8.0 이상 · Chrome 권장</p>
                <ol className="mt-4 list-inside list-decimal space-y-2 text-sm leading-6 text-foreground-700"><li>‘APK 다운로드’를 눌러 파일을 받으세요.</li><li>다운로드한 파일을 열고 휴대폰의 설치 안내를 따라주세요.</li><li>설치 후 Family Album을 열고 로그인하세요.</li></ol>
                <p className="mt-3 text-xs leading-5 text-foreground-500">휴대폰이 설치 허용을 요청하면 파일을 받은 브라우저의 ‘이 출처 허용’을 확인해 주세요. 설치가 제한된 기기는 아래 홈 화면 추가 방식을 사용할 수 있어요.</p>
              </section>
            ) : <p className="mt-4 rounded-2xl bg-secondary-100 p-4 text-sm leading-6 text-foreground-600">APK는 안드로이드 전용 파일이에요. 아이폰은 아래 안내에 따라 홈 화면에 추가해 사용할 수 있어요.</p>}
            {inApp && <p className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">지금은 앱 안의 브라우저로 열려 있어요. 링크를 복사해 {actualPlatform === 'ios' ? 'Safari' : 'Chrome'}에서 열어주세요.</p>}
            {actualPlatform === 'desktop' && <p className="mt-4 text-center text-xs leading-5 text-foreground-500">휴대폰으로 이 링크를 열면 해당 기기의 설치 안내가 표시돼요.</p>}
            <button type="button" onClick={install} disabled={installing} className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl border border-background-300 px-4 py-4 text-sm font-semibold text-foreground-800 disabled:opacity-50"><i className="ri-add-box-line" />{installing ? '설치창 여는 중…' : canInstall ? '홈 화면에 추가하기' : platform === 'ios' ? '아이폰 설치 방법 보기' : '파일 설치 대신 홈 화면에 추가하기'}</button>
            <section id="install-guide" role="tabpanel" aria-labelledby={`install-tab-${platform}`} ref={guideRef} className="mt-6 scroll-mt-5 rounded-3xl border border-background-200 bg-white p-5">
              <h2 className="text-lg font-bold">{platform === 'ios' ? '아이폰에 추가하기' : '안드로이드에 추가하기'}</h2>
              <ol className="mt-5 space-y-5">{steps.map(([icon, title, body], index) => <li key={title} className="flex gap-3"><span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent-100 text-sm font-bold text-accent-700">{index + 1}</span><div><h3 className="flex items-center gap-1.5 text-sm font-bold"><i className={icon} aria-hidden="true" />{title}</h3><p className="mt-1.5 text-xs leading-5 text-foreground-500">{body}</p></div></li>)}</ol>
            </section>
          </>
        )}

        <section className="mt-6 rounded-3xl bg-secondary-100 p-5">
          <h2 className="text-base font-bold">가족에게 설치 링크 보내기</h2>
          <p className="mt-1.5 text-xs leading-5 text-foreground-500">이 링크는 설치 안내만 공유해요. 앨범을 보려면 로그인하거나 가족에게 받은 초대 코드로 가입해 주세요.</p>
          <div className="mt-4 grid grid-cols-2 gap-2"><button type="button" onClick={() => void share()} disabled={sharing} className="rounded-xl bg-white px-3 py-3 text-sm font-semibold disabled:opacity-50"><i className="ri-share-forward-line mr-1" />링크 공유</button><button type="button" onClick={() => void copyLink()} className="rounded-xl border border-background-300 px-3 py-3 text-sm font-semibold"><i className="ri-link mr-1" />링크 복사</button></div>
          <input ref={linkRef} readOnly value={installUrl} aria-label="앱 설치 링크" onFocus={event => event.currentTarget.select()} className="mt-3 w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-xs text-foreground-600" />
        </section>
        <p role="status" aria-live="polite" className="mt-3 min-h-5 text-center text-xs leading-5 text-accent-700">{message}</p>
        <footer className="mt-5 text-center"><Link to="/" className="text-sm font-semibold text-foreground-700 underline underline-offset-4">가족 앨범으로 이동</Link><p className="mt-3 text-[11px] leading-5 text-foreground-400">사진을 보려면 인터넷 연결이 필요해요.<br />앱 내용은 홈페이지 업데이트와 함께 새로워져요.</p></footer>
      </div>
    </div>
  );
}
